import { SerializedError } from '@reduxjs/toolkit';
import { BaseQueryFn, MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { updateToken, kc as keycloak } from 'src/keycloak';
import { setAlert } from 'src/redux/slices/snackbar';
import { APITags } from 'src/services/utils';
import { ALERT } from 'src/types/enum';
import { ErrorMessages, ErrorContext, WarningCodes } from 'src/utils/errorMappings';
import logger from 'src/utils/logger';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import {
  INVALID_TOKEN_STATUS,
  INVALID_USER_STATUS,
  MAX_REQUEST_RETRIES,
  RETRYABLE_HTTP_STATUS_CODES,
  SESSION_EXPIRED_STATUS
} from 'src/utils/utils';
import { getRetryDelayMs } from './getRetryDelayMs';

const sleep = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

const useMutation = <T, R>({
  api,
  errorContext = ErrorContext.GENERAL,
  subContext,
  alertSkippingCodes,
  fixedCacheKey,
  maxRetries
}: {
  api: UseMutation<MutationDefinition<T, BaseQueryFn, APITags, R>>;
  errorContext: ErrorContext;
  subContext?: ErrorContext;
  alertSkippingCodes?: string[];
  fixedCacheKey?: string;
  maxRetries?: number;
}) => {
  const effectiveMaxRetries = maxRetries ?? (api as any).maxRetries ?? MAX_REQUEST_RETRIES;
  const [trigger, result] = api({ fixedCacheKey });
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const retryFailedCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastErrorRef = useRef<{ msg: string; timestamp: number } | null>(null);

  // Cancel any pending retry timer on unmount so it doesn't fire on the wrong screen.
  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  const customTrigger = async ({
    params,
    getParams,
    isRetry = false,
    abortContainer,
    fallbackMsg = T.internalServerError,
    successMsg,
    successCallback,
    errorCallback
  }: {
    params: T;
    getParams?: () => Promise<T> | T;
    isRetry?: boolean;
    abortContainer?: { abort: () => void };
    fallbackMsg?: string;
    successMsg?: string;
    successCallback?: ((data: R) => void) | undefined;
    errorCallback?: ((error: SerializedError) => void) | undefined;
  }): Promise<R> => {
    try {
      let lastErr: any;

      for (let attempt = 0; attempt <= effectiveMaxRetries; attempt++) {
        try {
          const shouldRebuildParams = getParams && (attempt > 0 || isRetry);
          const attemptParams = shouldRebuildParams ? await getParams() : params;
          const mutationPromise = trigger(attemptParams);

          if (abortContainer) {
            abortContainer.abort = () => mutationPromise.abort();
          }

          const response = await mutationPromise.unwrap();
          if (successMsg) {
            dispatch(setAlert({ type: ALERT.SUCCESS, msg: t(successMsg) }));
          }
          if (successCallback) successCallback(response);
          return response;
        } catch (err: any) {
          if (
            err?.name === 'AbortError' ||
            (err?.status === 'FETCH_ERROR' && err?.error === 'Aborted')
          ) {
            logger.log('Request explicitly cancelled by user.');
            if (errorCallback) errorCallback(err);
            return;
          }

          const status = err?.originalStatus || err?.data?.status;
          const isRetryable =
            RETRYABLE_HTTP_STATUS_CODES.includes(status) ||
            err?.status === 'FETCH_ERROR';

          if (isRetryable && attempt < effectiveMaxRetries) {
            await sleep(getRetryDelayMs(status, attempt, err));
            continue;
          }

          lastErr = err;
          break;
        }
      }

      throw lastErr;
    } catch (err) {
      if (
        (err?.status === INVALID_USER_STATUS ||
          err?.status === INVALID_TOKEN_STATUS) &&
        retryFailedCountRef.current < effectiveMaxRetries
      ) {
        logger.log('Invalid user/token, updating session now..',err?.status);
        const delay = getRetryDelayMs(err?.status, retryFailedCountRef.current, err);
        retryFailedCountRef.current += 1;
        return updateToken(keycloak).then(
          () =>
            new Promise<R>((resolve, reject) => {
              retryTimerRef.current = setTimeout(() => {
                logger.log(
                  `Retrying... Attempt ${retryFailedCountRef.current}`
                );
                customTrigger({
                  params,
                  getParams,
                  isRetry: true,
                  fallbackMsg,
                  successMsg,
                  successCallback,
                  errorCallback
                }).then(resolve, reject);
              }, delay);
            })
        );
      } else if (err?.status === SESSION_EXPIRED_STATUS) {
        logger.log('Invalid user/token, updating session now..',err?.status);
        const delay = getRetryDelayMs(err?.status, retryFailedCountRef.current, err);
        return updateToken(keycloak).then(
          () =>
            new Promise<R>((resolve, reject) => {
              setTimeout(() => {
                logger.log(
                  `Retrying... Attempt ${retryFailedCountRef.current}`
                );
                customTrigger({
                  params,
                  getParams,
                  isRetry: true,
                  fallbackMsg,
                  successMsg,
                  successCallback,
                  errorCallback
                }).then(resolve, reject);
              }, delay);
            })
        );
      } else {
        retryFailedCountRef.current = 0;
        if (alertSkippingCodes && alertSkippingCodes.includes(err?.status)) {
        } else {
          const errorMsg =
            ErrorMessages[errorContext]?.[subContext]?.[err.status] ||
            ErrorMessages[errorContext]?.[err.status] ||
            ErrorMessages[ErrorContext.GENERAL][err.status] ||
            fallbackMsg;

          const translatedMsg = t(errorMsg);
          const now = Date.now();

          const isDuplicate =
            lastErrorRef.current &&
            lastErrorRef.current.msg === translatedMsg &&
            now - lastErrorRef.current.timestamp < 2000;

          if (!isDuplicate) {
            const alertType = WarningCodes.has(err?.status) ? ALERT.WARNING : ALERT.ERROR;
            dispatch(setAlert({ type: alertType, msg: translatedMsg }));
            lastErrorRef.current = { msg: translatedMsg, timestamp: now };
          }
        }

        if (errorCallback) errorCallback(err);
      }
    }
  };
  return [customTrigger, result] as const;
};

export default useMutation;
